import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, Icon, Skeleton } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { formatBytes, formatNumber } from "../shared/format";
import type { SourceUsage } from "../shared/types";

/**
 * "Questions it answers": the API has no per-source question/helpfulness list yet, so this is an
 * honest empty state with the real citation count and a route to Knowledge Gaps.
 */
export function QuestionsPanel({ usage }: { usage: SourceUsage | undefined }) {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const k = "deployKnowledge.sourceDetail.questions";
  return (
    <EmptyState
      icon="message-circle"
      headingLevel={3}
      title={t(`${k}.emptyTitle`)}
      description={t(`${k}.emptyBody`, { count: formatNumber(usage?.citations ?? 0, i18n.language) })}
      action={
        <Button asChild variant="secondary">
          <AppLink href={consoleRoutes.gaps(summary.slug)}>
            <Icon name="target" />
            {t(`${k}.improve`)}
          </AppLink>
        </Button>
      }
    />
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-2 p-3">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

/** Usage totals from `GET /v1/sources/{id}/usage` (no daily series / channel split in the API yet). */
export function UsagePanel({ usage, isPending }: { usage: SourceUsage | undefined; isPending: boolean }) {
  const { t, i18n } = useTranslation();
  const { summary } = useWorkspaceContext();
  const k = "deployKnowledge.sourceDetail.usage";
  const n = (value: number) => formatNumber(value, i18n.language);
  if (isPending || !usage) {
    return (
      <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3" aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-[72px]" />
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-5 p-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile label={t(`${k}.citations`)} value={n(usage.citations)} />
        <Tile label={t(`${k}.agents`)} value={n(usage.agents.length)} />
        <Tile label={t(`${k}.chunks`)} value={n(usage.chunks)} />
        <Tile label={t(`${k}.tokens`)} value={n(usage.tokens)} />
        <Tile label={t(`${k}.storage`)} value={formatBytes(usage.storage_bytes)} />
        <Tile label={t(`${k}.generatedQuestions`)} value={n(usage.questions)} />
      </div>
      <div className="rounded-md border border-line p-3">
        <h3 className="text-xs font-semibold text-fg-muted">{t(`${k}.byAgent`)}</h3>
        {usage.agents.length === 0 ? (
          <p className="mt-2 text-sm text-fg-muted">{t(`${k}.noAgents`)}</p>
        ) : (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {usage.agents.map((agent) => (
              <li key={agent.id}>
                <AppLink href={consoleRoutes.knowledge(summary.slug, agent.id)} className="badge badge-neutral hover:underline">
                  {agent.name}
                </AppLink>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <Icon name="info" className="mt-0.5 size-3.5 shrink-0" />
        {t(`${k}.trendNote`)}
      </p>
    </div>
  );
}
